import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lotteryApi, type LotteryConfig } from '../../api/lottery.js';
import { ApiError } from '../../api/client.js';
import { Spinner, ErrorBox } from '../../components/ui.js';
import { hasFieldErrors, inputCls, type FieldErrors } from '../../lib/formValidation.js';
import { FormRow } from './FormRow.js';

/**
 * 원본: lottery/product/settings.php (랜덤박스 목록 위 '설정' 버튼).
 * lottery_config 한 줄(cf_id=1)이라 FANZA 랜덤박스 전체에 걸린다.
 *
 * 원본처럼 '사용안함'을 고르면 매수 줄만 숨기고 값은 그대로 저장한다.
 */

/** DB 컬럼 크기 - server lottery.schemas.ts 의 LOTTERY_CONFIG_*_NUM_MAX 와 같다. */
const EXCHANGE_NUM_MAX = 127;
const BAKUGAI_NUM_MAX = 255;

interface SettingsForm {
  exchange: 0 | 1;
  exchangeNum: string;
  bakugai: 0 | 1;
  bakugaiNum: string;
  bannerImg: string;
}

const toForm = (c: LotteryConfig): SettingsForm => ({
  exchange: c.exchange,
  exchangeNum: String(c.exchangeNum),
  bakugai: c.bakugai,
  bakugaiNum: String(c.bakugaiNum),
  bannerImg: c.bannerImg,
});

function checkCount(value: string, max: number): string | undefined {
  if (value.trim() === '') return '매수를 입력해주세요.';
  if (!/^\d+$/.test(value.trim())) return '숫자만 입력해주세요.';
  const v = Number(value);
  if (v < 1 || v > max) return `1 ~ ${max} 사이로 입력해주세요.`;
  return undefined;
}

function checkBanner(value: string): string | undefined {
  const v = value.trim();
  if (v.length > 255) return '255자 이하로 입력해주세요.';
  if (v !== '' && !/^(https?:\/\/|\/)\S*$/i.test(v)) return 'http(s) 주소나 / 로 시작하는 경로를 입력해주세요.';
  return undefined;
}

export function LotterySettingsModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  // 다른 사람이 바꿨을 수 있으니 열 때마다 새로 읽는다.
  const config = useQuery({
    queryKey: ['lottery', 'config'],
    queryFn: () => lotteryApi.config(),
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">설정</h2>

        {config.isPending ? (
          <Spinner />
        ) : config.isError ? (
          <ErrorBox error={config.error} onRetry={() => void config.refetch()} />
        ) : (
          <SettingsForm initial={config.data} onSaved={onSaved} />
        )}
      </div>
    </div>
  );
}

function OnOffRadios({
  name,
  label,
  value,
  onChange,
}: {
  name: string;
  label: string;
  value: 0 | 1;
  onChange: (v: 0 | 1) => void;
}) {
  return (
    <span className="lottery-prize-form__radios" role="radiogroup" aria-label={label}>
      <label className="radio-label">
        <input type="radio" name={name} checked={value === 1} onChange={() => onChange(1)} />
        사용
      </label>
      <label className="radio-label">
        <input type="radio" name={name} checked={value === 0} onChange={() => onChange(0)} />
        사용안함
      </label>
    </span>
  );
}

function SettingsForm({ initial, onSaved }: { initial: LotteryConfig; onSaved: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SettingsForm>(() => toForm(initial));
  const [submitted, setSubmitted] = useState(false);
  const set = <K extends keyof SettingsForm>(key: K) => (value: SettingsForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const exchangeOn = form.exchange === 1;
  const bakugaiOn = form.bakugai === 1;

  // 숨긴 매수 줄은 검사하지 않는다 - 대신 고치다 만 값이 아니라 읽어온 값을 보낸다.
  const errors: FieldErrors = {
    exchangeNum: exchangeOn ? checkCount(form.exchangeNum, EXCHANGE_NUM_MAX) : undefined,
    bakugaiNum: bakugaiOn ? checkCount(form.bakugaiNum, BAKUGAI_NUM_MAX) : undefined,
    bannerImg: checkBanner(form.bannerImg),
  };
  const shown: FieldErrors = submitted ? errors : {};

  const save = useMutation({
    mutationFn: () =>
      lotteryApi.updateConfig({
        exchange: form.exchange,
        exchangeNum: exchangeOn ? Number(form.exchangeNum) : initial.exchangeNum,
        bakugai: form.bakugai,
        bakugaiNum: bakugaiOn ? Number(form.bakugaiNum) : initial.bakugaiNum,
        bannerImg: form.bannerImg.trim(),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['lottery', 'config'], saved);
      onSaved();
    },
  });

  const banner = form.bannerImg.trim();
  const [brokenPreview, setBrokenPreview] = useState<string | null>(null);

  return (
    <form
      className="lottery-prize-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        if (hasFieldErrors(errors)) return;
        save.mutate();
      }}
    >
      <div className="lottery-prize-form__table">
        <FormRow label="교환기능 설정" as="div">
          <OnOffRadios name="cf_exchange" label="교환기능 설정" value={form.exchange} onChange={set('exchange')} />
        </FormRow>
        {exchangeOn && (
          <FormRow label="교환기능 매수" error={shown.exchangeNum}>
            <input
              className={`${inputCls(shown.exchangeNum)} lottery-prize-form__short`}
              aria-label="교환기능 매수"
              type="number"
              inputMode="numeric"
              min={1}
              max={EXCHANGE_NUM_MAX}
              value={form.exchangeNum}
              placeholder="매수 입력"
              onChange={(e) => set('exchangeNum')(e.target.value)}
            />
            <span className="lottery-prize-form__hint">최대 {EXCHANGE_NUM_MAX}</span>
          </FormRow>
        )}

        <FormRow label="폭탄구매 설정" as="div">
          <OnOffRadios name="cf_bakugai" label="폭탄구매 설정" value={form.bakugai} onChange={set('bakugai')} />
        </FormRow>
        {bakugaiOn && (
          <FormRow label="폭탄구매 매수" error={shown.bakugaiNum}>
            <input
              className={`${inputCls(shown.bakugaiNum)} lottery-prize-form__short`}
              aria-label="폭탄구매 매수"
              type="number"
              inputMode="numeric"
              min={1}
              max={BAKUGAI_NUM_MAX}
              value={form.bakugaiNum}
              placeholder="매수 입력"
              onChange={(e) => set('bakugaiNum')(e.target.value)}
            />
            <span className="lottery-prize-form__hint">최대 {BAKUGAI_NUM_MAX}</span>
          </FormRow>
        )}

        <FormRow label="배너 이미지 (URL/경로)" error={shown.bannerImg}>
          <input
            className={inputCls(shown.bannerImg)}
            value={form.bannerImg}
            maxLength={255}
            placeholder="예: /images/banner.png 또는 http://..."
            onChange={(e) => set('bannerImg')(e.target.value)}
          />
          {/* 상대 경로는 쇼핑몰 기준이라 여기서는 못 그린다 - 전체 주소일 때만 미리 본다. */}
          {/^https?:\/\/\S+$/i.test(banner) && brokenPreview !== banner && (
            <span className="lottery-settings__banner">
              <img src={banner} alt="배너 미리보기" onError={() => setBrokenPreview(banner)} />
            </span>
          )}
        </FormRow>
      </div>

      {save.isError && (
        <div className="qty-msg qty-msg--error">
          {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
        </div>
      )}

      <button type="submit" className="btn btn--primary btn--block" disabled={save.isPending}>
        {save.isPending ? '저장 중…' : '수정하기'}
      </button>
    </form>
  );
}
